import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core'

/**
 * Publishes an element's measured height as a CSS custom property, so sticky siblings can
 * offset themselves below it instead of hardcoding a pixel guess. These heights are font
 * metric dependent and shift on webfont load and at mobile breakpoints.
 *
 * Usage:
 * <div class="header-container" stickyHeight>...</div>
 * <div class="tabs-container" stickyHeight="--sticky-tabs-height">...</div>
 */
@Directive({
  selector: '[stickyHeight]',
  standalone: false,
})
export class StickyHeightDirective implements AfterViewInit, OnDestroy {
  @Input() stickyHeight?: string

  // Every instance writing the same property shares one slot on the root element, so teardown
  // has to hand ownership back instead of clearing a value a live instance still owns.
  private static owners = new Map<string, StickyHeightDirective[]>()

  private observer?: ResizeObserver
  private published = -1

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const owners = StickyHeightDirective.owners.get(this.propertyName) ?? []
    owners.push(this)
    StickyHeightDirective.owners.set(this.propertyName, owners)

    // Publish before the observer's first callback so the offset is correct on the very
    // first paint, not one frame late.
    this.publish()

    // Setting a custom property is a direct DOM write with no bindings behind it, so this
    // never needs change detection — and the observer fires on every reflow.
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.publish())
      this.observer.observe(this.el.nativeElement)
    })
  }

  ngOnDestroy(): void {
    this.observer?.disconnect()

    const remaining = (StickyHeightDirective.owners.get(this.propertyName) ?? []).filter((o) => o !== this)
    if (remaining.length === 0) {
      StickyHeightDirective.owners.delete(this.propertyName)
      // Clear it so the next page without this element falls back to the stylesheet default
      // rather than inheriting a stale offset.
      document.documentElement.style.removeProperty(this.propertyName)
      return
    }

    StickyHeightDirective.owners.set(this.propertyName, remaining)
    // Republish from a survivor, resetting its memo first: nothing resizes it on our way out,
    // so without this the property stays cleared and the page sits on the stylesheet default.
    const survivor = remaining[remaining.length - 1]
    survivor.published = -1
    survivor.publish()
  }

  private get propertyName(): string {
    return this.stickyHeight || '--sticky-header-height'
  }

  private publish(): void {
    // Floor rather than round: consumers then sit a sub-pixel high and tuck under this
    // element, where rounding up would leave a hairline for page content to show through.
    const height = Math.floor(this.el.nativeElement.getBoundingClientRect().height)
    if (height === this.published) {
      return
    }
    this.published = height
    document.documentElement.style.setProperty(this.propertyName, `${height}px`)
  }
}
